import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { STATUS_LABEL } from "@/lib/constants";
import { RefreshCw, RotateCcw, Search, UserPlus, UserMinus, Pencil, CheckCircle2, AlertCircle, Clock, Check, X, PauseCircle, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import BuktiFilesPreview from "@/components/BuktiFilesPreview";

const TIPE_ICONS = { BARU: UserPlus, STATUS: UserMinus, UBAH: Pencil, PINDAH_TPS: ArrowRightLeft };
const TIPE_LABELS = { BARU: "Tambahan Baru", STATUS: "TMS/Status", UBAH: "Perubahan Data", PINDAH_TPS: "Pindah TPS" };
const STATUS_UI = {
  APPLIED: { label: "Diterapkan", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  REVERTED: { label: "Dikembalikan", color: "bg-red-100 text-red-800", icon: RotateCcw },
  PENDING: { label: "Menunggu", color: "bg-amber-100 text-amber-800", icon: Clock },
  APPROVED: { label: "Disetujui", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  REJECTED: { label: "Ditolak", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

/**
 * Halaman "Log Perubahan PPDP" untuk Admin & Panitia.
 * Menampilkan semua voter_changes lintas PPDP + tombol "Kembalikan" untuk yang status=APPLIED.
 */
export default function PpdpChangesReviewPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("NEEDS_REVIEW");
  const [tipeFilter, setTipeFilter] = useState("all");
  const [q, setQ] = useState("");
  // Unified action dialog: kind in ("approve","revert","hold","reject")
  const [action, setAction] = useState(null); // { kind, item }
  const [actionReason, setActionReason] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  // Bulk selection state
  const [selected, setSelected] = useState(() => new Set());
  const [bulkKind, setBulkKind] = useState(null); // "approve" | "revert" | "hold" | "reject"
  const [bulkReason, setBulkReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, failed: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      // "NEEDS_REVIEW" adalah alias UI untuk gabungan APPLIED + PENDING (semua yang butuh keputusan Panitia)
      if (statusFilter === "NEEDS_REVIEW") {
        params.status = "APPLIED,PENDING";
      } else if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      // Filter "MENINGGAL" / "PERLU_VERIFIKASI" → include STATUS + UBAH (Ubah Data mode juga bisa ganti status)
      // + server-side filter data_baru.status agar hasil match total (bukan artefak page 500)
      if (tipeFilter === "MENINGGAL" || tipeFilter === "PERLU_VERIFIKASI") {
        params.tipe = "BARU,STATUS,UBAH";
        params.data_baru_status = tipeFilter;
      } else if (tipeFilter !== "all") {
        params.tipe = tipeFilter;
      }
      const { data } = await api.get("/voter-changes", { params });
      setItems(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); setSelected(new Set()); /* eslint-disable-next-line */ }, [statusFilter, tipeFilter]);

  const filtered = useMemo(() => {
    let list = items;
    if (tipeFilter === "MENINGGAL") {
      list = list.filter((it) => ((it.data_baru || {}).status || "").toUpperCase() === "MENINGGAL");
    }
    if (tipeFilter === "PERLU_VERIFIKASI") {
      list = list.filter((it) => ((it.data_baru || {}).status || "").toUpperCase() === "PERLU_VERIFIKASI");
    }
    const s = q.trim().toLowerCase();
    if (!s) return list;
    return list.filter((it) => {
      const v = it.voter_current || it.voter_snapshot || {};
      const n = it.data_baru || {};
      return (
        (v.nama || n.nama || "").toLowerCase().includes(s) ||
        (v.nik || n.nik || "").includes(s) ||
        (v.tps || n.tps || "").toLowerCase().includes(s) ||
        (it.submitted_by_username || "").toLowerCase().includes(s)
      );
    });
  }, [items, q, tipeFilter]);

  const counts = useMemo(() => {
    const c = { APPLIED: 0, APPROVED: 0, REVERTED: 0, PENDING: 0, REJECTED: 0 };
    for (const i of items) c[i.status] = (c[i.status] || 0) + 1;
    return c;
  }, [items]);

  const ACTION_CONFIG = {
    approve: {
      label: "Setujui", url: "approve", body: (r) => ({ catatan: r || null }),
      color: "bg-emerald-700 hover:bg-emerald-800 text-white", icon: Check, requireReason: false,
      title: (it) => it.tipe === "BARU" ? "Setujui Pemilih Baru" : "Setujui Perubahan Data",
      desc: (it) => it.tipe === "BARU"
        ? "Pemilih akan dimasukkan ke DPS (voter dibuat)."
        : "Perubahan data dianggap final. Data pemilih tetap seperti sekarang.",
    },
    revert: {
      label: "Batalkan", url: "revert", body: (r) => ({ catatan: r || null }),
      color: "bg-slate-700 hover:bg-slate-800 text-white", icon: RotateCcw, requireReason: false,
      title: (it) => it.tipe === "BARU" ? "Batalkan & Hapus Pemilih" : "Batalkan Perubahan Data",
      desc: (it) => it.tipe === "BARU"
        ? "Voter yang sudah dibuat akan DIHAPUS dari DPS. Aksi ini yang benar untuk membuang pemilih dari Tambahan Delta."
        : "Field data direstore ke nilai sebelumnya. Voter TIDAK dihapus.",
    },
    hold: {
      label: "Menunggu", url: "hold", body: (r) => ({ catatan: r || null }),
      color: "bg-amber-600 hover:bg-amber-700 text-white", icon: PauseCircle, requireReason: false,
      title: () => "Tandai Menunggu Persetujuan",
      desc: () => "Un-apply perubahan, tandai PENDING untuk verifikasi lebih lanjut. Data direstore sementara.",
    },
    reject: {
      label: "Tolak", url: "reject", body: (r) => ({ catatan: r || "" }),
      color: "bg-red-600 hover:bg-red-700 text-white", icon: X, requireReason: true,
      title: (it) => it.tipe === "BARU" ? "Tolak Pemilih Baru" : "Tolak Perubahan Data",
      desc: (it) => it.tipe === "BARU"
        ? "Pemilih tidak dimasukkan ke DPS (voter tidak dibuat / dihapus jika sudah APPLIED). Wajib beri alasan."
        : "Hanya field yang berubah dikembalikan; voter TIDAK dihapus. Untuk menghapus voter, batalkan entri BARU-nya. Wajib beri alasan.",
    },
  };

  const openAction = (kind, item) => {
    setAction({ kind, item });
    setActionReason("");
  };

  // ---- Bulk selection helpers ----
  const isSelected = (id) => selected.has(id);
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());
  const availableActionsFor = (it) => {
    if (it.status === "APPLIED") {
      return it.tipe === "BARU" ? ["approve", "reject"] : ["approve", "revert", "hold", "reject"];
    }
    if (it.status === "PENDING") return ["approve", "reject"];
    if (it.status === "APPROVED") return ["revert"];
    return [];
  };
  const selectableFiltered = useMemo(
    () => filtered.filter((it) => availableActionsFor(it).length > 0),
    [filtered],
  );
  const allSelectableChecked = selectableFiltered.length > 0 &&
    selectableFiltered.every((it) => selected.has(it.id));
  const toggleSelectAll = () => {
    if (allSelectableChecked) {
      // Unselect all currently visible-selectable
      const ids = new Set(selectableFiltered.map((x) => x.id));
      setSelected((prev) => new Set([...prev].filter((id) => !ids.has(id))));
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        selectableFiltered.forEach((it) => next.add(it.id));
        return next;
      });
    }
  };
  // Items yang bisa di-bulk untuk aksi tertentu
  const bulkTargets = useMemo(() => {
    if (!bulkKind) return [];
    return filtered.filter((it) => selected.has(it.id) && availableActionsFor(it).includes(bulkKind));
  }, [filtered, selected, bulkKind]);
  const bulkSkipped = selected.size - bulkTargets.length;

  const doBulkAction = async () => {
    if (!bulkKind || bulkTargets.length === 0) return;
    const cfg = ACTION_CONFIG[bulkKind];
    if (cfg.requireReason && !bulkReason.trim()) {
      toast.error("Alasan wajib diisi.");
      return;
    }
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: bulkTargets.length, failed: 0 });
    const payload = cfg.body(bulkReason.trim());
    // Concurrency limit 5 agar tidak overwhelm backend
    const CONCURRENCY = 5;
    let doneCount = 0;
    let failedCount = 0;
    const queue = [...bulkTargets];
    const runOne = async () => {
      while (queue.length) {
        const it = queue.shift();
        try {
          await api.post(`/voter-changes/${it.id}/${cfg.url}`, payload);
        } catch (_e) {
          failedCount += 1;
        }
        doneCount += 1;
        setBulkProgress({ done: doneCount, total: bulkTargets.length, failed: failedCount });
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, bulkTargets.length) }, runOne));
    setBulkBusy(false);
    if (failedCount === 0) {
      toast.success(`${cfg.label}: ${doneCount} pengajuan diproses.`);
    } else {
      toast.error(`${cfg.label}: ${doneCount - failedCount} sukses, ${failedCount} gagal.`);
    }
    setBulkKind(null);
    setBulkReason("");
    clearSelection();
    load();
  };

  const doAction = async () => {
    if (!action) return;
    const cfg = ACTION_CONFIG[action.kind];
    if (!cfg) return;
    if (cfg.requireReason && !actionReason.trim()) {
      toast.error("Alasan wajib diisi.");
      return;
    }
    setActionBusy(true);
    try {
      await api.post(`/voter-changes/${action.item.id}/${cfg.url}`, cfg.body(actionReason.trim()));
      toast.success(`${cfg.label}: berhasil.`);
      setAction(null);
      setActionReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActionBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="ppdp-changes-review-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-semibold">Panitia / Admin</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">Log Perubahan PPDP</h1>
          <p className="text-slate-600 text-sm mt-2 max-w-2xl">
            Semua CRUD dari PPDP tercatat di sini. Klik <b>Kembalikan</b> untuk mengurungkan perubahan yang salah.
            Perubahan hanya bisa di-revert selama TPS masih fase <b>DPS</b>.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="btn-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { s: "APPLIED", label: "Diterapkan", color: "emerald", hint: "Direct-apply PPDP, menunggu review" },
          { s: "PENDING", label: "Menunggu", color: "amber", hint: "Antrean BARU / TPS locked" },
          { s: "APPROVED", label: "Disetujui", color: "emerald", hint: "Final resmi" },
          { s: "REVERTED", label: "Dikembalikan", color: "slate", hint: "Panitia batalkan" },
          { s: "REJECTED", label: "Ditolak", color: "red", hint: "Ditolak Panitia" },
        ].map(({ s, label, color, hint }) => (
          <Card key={s}><CardContent className="pt-5">
            <div className={`text-[10px] uppercase tracking-[0.18em] text-${color}-700 font-semibold`}>{label}</div>
            <div className="text-3xl font-bold tabular-nums" data-testid={`kpi-${s.toLowerCase()}`}>{counts[s] || 0}</div>
            <div className="text-[10px] text-slate-500 mt-1">{hint}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari nama, NIK, TPS, PPDP..."
            className="pl-9"
            data-testid="input-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
          data-testid="filter-status"
        >
          <option value="NEEDS_REVIEW">Perlu Review (default): APPLIED + PENDING</option>
          <option value="APPLIED">Diterapkan (menunggu review)</option>
          <option value="PENDING">Menunggu Persetujuan (BARU)</option>
          <option value="APPROVED">Disetujui</option>
          <option value="REVERTED">Dikembalikan</option>
          <option value="REJECTED">Ditolak</option>
          <option value="all">Semua Status</option>
        </select>
        <select
          value={tipeFilter}
          onChange={(e) => setTipeFilter(e.target.value)}
          className="h-9 rounded-md border border-slate-300 px-3 text-sm bg-white"
          data-testid="filter-tipe"
        >
          <option value="all">Semua Tipe</option>
          <option value="BARU">Tambahan Baru</option>
          <option value="STATUS">TMS/Status</option>
          <option value="UBAH">Ubah Data</option>
          <option value="PINDAH_TPS">Pindah TPS</option>
          <option value="MENINGGAL">— Khusus: Meninggal Dunia</option>
          <option value="PERLU_VERIFIKASI">— Khusus: Perlu Verifikasi</option>
        </select>
        <div className="text-xs text-slate-500 ml-auto">Menampilkan {filtered.length} dari {items.length}</div>
      </div>

      {/* Bulk action bar (muncul saat ada item terpilih ATAU ada item yg bisa dipilih) */}
      {selectableFiltered.length > 0 && (
        <div className="sticky top-2 z-20 rounded-md border border-slate-300 bg-white/95 backdrop-blur px-3 py-2 shadow-sm flex flex-wrap items-center gap-2" data-testid="bulk-actions-bar">
          <Checkbox
            id="bulk-select-all"
            checked={allSelectableChecked}
            onCheckedChange={toggleSelectAll}
            data-testid="bulk-select-all"
          />
          <label htmlFor="bulk-select-all" className="text-sm font-semibold cursor-pointer select-none">
            {selected.size > 0
              ? <>Terpilih <span data-testid="bulk-selected-count">{selected.size}</span> · {allSelectableChecked ? "Semua yang tampil" : "sebagian"}</>
              : <>Pilih semua ({selectableFiltered.length} bisa diproses)</>
            }
          </label>
          {selected.size > 0 && (
            <>
              <Button size="sm" variant="ghost" onClick={clearSelection} className="text-slate-500 hover:text-slate-900" data-testid="bulk-clear">
                Batal pilih
              </Button>
              <div className="ml-auto flex flex-wrap gap-1.5">
                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => { setBulkKind("approve"); setBulkReason(""); }} data-testid="bulk-approve">
                  <Check className="h-3.5 w-3.5 mr-1" /> Setujui Massal
                </Button>
                <Button size="sm" className="bg-slate-700 hover:bg-slate-800 text-white" onClick={() => { setBulkKind("revert"); setBulkReason(""); }} data-testid="bulk-revert">
                  <RotateCcw className="h-3.5 w-3.5 mr-1" /> Batalkan Massal
                </Button>
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { setBulkKind("hold"); setBulkReason(""); }} data-testid="bulk-hold">
                  <PauseCircle className="h-3.5 w-3.5 mr-1" /> Tahan Massal
                </Button>
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setBulkKind("reject"); setBulkReason(""); }} data-testid="bulk-reject">
                  <X className="h-3.5 w-3.5 mr-1" /> Tolak Massal
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {loading && <div className="text-sm text-slate-500 py-6 text-center">Memuat...</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-sm text-slate-500 p-8 text-center border border-dashed border-slate-300 rounded-md">
            Tidak ada perubahan yang cocok filter.
          </div>
        )}
        {filtered.map((it) => {
          const Icon = TIPE_ICONS[it.tipe] || Pencil;
          const su = STATUS_UI[it.status] || STATUS_UI.PENDING;
          const StatusIcon = su.icon;
          const voter = it.voter_current || it.voter_snapshot || {};
          const newData = it.data_baru || {};
          const isBaru = it.tipe === "BARU";
          // Actions available depending on tipe + current status
          let actions = [];
          if (it.status === "APPLIED") {
            actions = isBaru
              ? ["approve", "reject"]
              : ["approve", "revert", "hold", "reject"];
          } else if (it.status === "PENDING") {
            actions = ["approve", "reject"];
          } else if (it.status === "APPROVED") {
            // Salah setujui → Batalkan (revert)
            actions = ["revert"];
          }
          return (
            <article key={it.id} className={`rounded-md border ${isSelected(it.id) ? "border-blue-400 bg-blue-50/30" : "border-slate-200 bg-white"} p-4`} data-testid={`change-${it.id}`}>
              <div className="flex items-start gap-3">
                {actions.length > 0 && (
                  <Checkbox
                    className="mt-1 shrink-0"
                    checked={isSelected(it.id)}
                    onCheckedChange={() => toggleSelect(it.id)}
                    data-testid={`select-${it.id}`}
                  />
                )}
                <div className="flex items-start justify-between gap-3 flex-wrap flex-1 min-w-0">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon className="h-4 w-4 text-slate-500" />
                    <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{TIPE_LABELS[it.tipe]}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full ${su.color} px-2 py-0.5 text-[11px] font-semibold`}>
                      <StatusIcon className="h-3 w-3" /> {su.label}
                    </span>
                    <BuktiFilesPreview files={it.bukti_files || []} testid={`bukti-${it.id}`} />
                  </div>
                  <div className="font-display font-bold text-base mt-1">{voter.nama || newData.nama || "Data pemilih"}</div>
                  <div className="text-xs font-mono text-slate-500 mt-0.5">{voter.nik || newData.nik}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {voter.tps || newData.tps} · RT {voter.rt || newData.rt}/RW {voter.rw || newData.rw} · {voter.dusun || newData.dusun}
                  </div>
                </div>
                <div className="text-right text-xs shrink-0">
                  <div className="text-slate-700 font-semibold">{it.submitted_by_name || it.submitted_by_username}</div>
                  <div className="text-slate-500">{it.submitted_at ? new Date(it.submitted_at).toLocaleString("id-ID") : ""}</div>
                  {actions.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 justify-end" data-testid={`actions-${it.id}`}>
                      {actions.map((k) => {
                        const cfg = ACTION_CONFIG[k];
                        const AIcon = cfg.icon;
                        return (
                          <Button
                            key={k}
                            size="sm"
                            variant="outline"
                            className={
                              k === "approve" ? "text-emerald-700 border-emerald-300 hover:bg-emerald-50" :
                              k === "revert"  ? "text-slate-700 border-slate-300 hover:bg-slate-50" :
                              k === "hold"    ? "text-amber-800 border-amber-300 hover:bg-amber-50" :
                                                "text-red-700 border-red-300 hover:bg-red-50"
                            }
                            onClick={() => openAction(k, it)}
                            data-testid={`btn-${k}-${it.id}`}
                          >
                            <AIcon className="h-3 w-3 mr-1" /> {cfg.label}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  {it.reverted_at && (
                    <div className="text-red-700 mt-1">
                      Direvert oleh {it.reverted_by_username}
                    </div>
                  )}
                </div>
                </div>
              </div>

              <div className="mt-3 text-xs bg-slate-50 rounded p-2.5 border border-slate-100">
                {it.tipe === "BARU" && (
                  <div>
                    <span className="text-slate-500">Data pemilih baru:</span>{" "}
                    <b>{newData.nama}</b> · JK {newData.jenis_kelamin} · {newData.alamat}
                  </div>
                )}
                {it.tipe === "PINDAH_TPS" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <div className="text-slate-500 uppercase text-[10px] tracking-wider mb-0.5">Sebelum</div>
                      <div><b>{(it.data_lama || {}).tps}</b> · RT {(it.data_lama || {}).rt}/RW {(it.data_lama || {}).rw}</div>
                      <div className="text-slate-600 truncate">{(it.data_lama || {}).dusun}</div>
                      <div className="text-slate-600 truncate">{(it.data_lama || {}).alamat}</div>
                    </div>
                    <div>
                      <div className="text-slate-500 uppercase text-[10px] tracking-wider mb-0.5">Setelah</div>
                      <div><b>{(it.data_baru || {}).tps}</b> · RT {(it.data_baru || {}).rt}/RW {(it.data_baru || {}).rw}</div>
                      <div className="text-slate-600 truncate">{(it.data_baru || {}).dusun}</div>
                      <div className="text-slate-600 truncate">{(it.data_baru || {}).alamat}</div>
                    </div>
                  </div>
                )}
                {(it.tipe === "STATUS" || it.tipe === "UBAH") && (
                  <div>
                    {Object.entries(newData).filter(([, v]) => v !== null && v !== undefined && v !== "").map(([k, v]) => (
                      <div key={k} className="grid grid-cols-3 gap-2">
                        <span className="text-slate-500">{k}:</span>
                        <span className="line-through text-slate-400 truncate">
                          {k === "status" ? (STATUS_LABEL[it.voter_snapshot?.[k]] || it.voter_snapshot?.[k]) : it.voter_snapshot?.[k]}
                        </span>
                        <span className="font-semibold truncate">
                          {k === "status" ? (STATUS_LABEL[v] || v) : v}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {it.alasan && <div className="mt-1 pt-1 border-t border-slate-200 text-slate-600"><span className="text-slate-500">Alasan PPDP:</span> {it.alasan}</div>}
                {it.revert_reason && <div className="text-red-700 mt-1"><span className="text-slate-500">Catatan revert:</span> {it.revert_reason}</div>}
                {it.catatan && it.status === "REJECTED" && <div className="text-red-700 mt-1"><span className="text-slate-500">Alasan penolakan:</span> {it.catatan}</div>}
                {it.hold_reason && <div className="text-amber-800 mt-1"><span className="text-slate-500">Catatan tahan:</span> {it.hold_reason}</div>}
              </div>
            </article>
          );
        })}
      </div>

      {/* Unified action dialog */}
      <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setActionReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="action-dialog">
          {action && (() => {
            const cfg = ACTION_CONFIG[action.kind];
            const AIcon = cfg.icon;
            const voter = action.item.voter_current || action.item.voter_snapshot || action.item.data_baru || {};
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                    <AIcon className="h-5 w-5" /> {typeof cfg.title === "function" ? cfg.title(action.item) : cfg.title}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="text-slate-600">{typeof cfg.desc === "function" ? cfg.desc(action.item) : cfg.desc}</div>
                  <div className="text-xs bg-slate-50 p-2.5 rounded border border-slate-200">
                    <div><b>{voter.nama}</b></div>
                    <div className="font-mono text-slate-500">{voter.nik}</div>
                    <div className="text-slate-500">oleh {action.item.submitted_by_username} · {action.item.submitted_at ? new Date(action.item.submitted_at).toLocaleString("id-ID") : ""}</div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">
                      {cfg.requireReason ? "Alasan (wajib) *" : "Catatan (opsional)"}
                    </label>
                    <Input
                      value={actionReason}
                      onChange={(e) => setActionReason(e.target.value)}
                      placeholder={cfg.requireReason ? "Contoh: Data tidak sesuai bukti..." : "Catatan tambahan..."}
                      data-testid="input-action-reason"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setAction(null); setActionReason(""); }}>Batal</Button>
                  <Button
                    className={cfg.color}
                    disabled={actionBusy}
                    onClick={doAction}
                    data-testid="btn-confirm-action"
                  >
                    <AIcon className="h-4 w-4 mr-2" /> {actionBusy ? "Memproses..." : cfg.label}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* Bulk action dialog */}
      <Dialog open={!!bulkKind} onOpenChange={(v) => { if (!v && !bulkBusy) { setBulkKind(null); setBulkReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="bulk-dialog">
          {bulkKind && (() => {
            const cfg = ACTION_CONFIG[bulkKind];
            const AIcon = cfg.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display tracking-tight flex items-center gap-2">
                    <AIcon className="h-5 w-5" /> {cfg.label} Massal
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="text-slate-700">
                    Akan diproses: <b data-testid="bulk-target-count">{bulkTargets.length}</b> pengajuan.
                    {bulkSkipped > 0 && (
                      <span className="block text-amber-800 mt-1">
                        {bulkSkipped} pengajuan dilewati karena statusnya tidak mendukung aksi &quot;{cfg.label}&quot;.
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded border border-slate-200">
                    {typeof cfg.desc === "function" ? cfg.desc({ tipe: "MIXED" }) : cfg.desc}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-semibold text-slate-700">
                      {cfg.requireReason ? "Alasan (wajib) *" : "Catatan (opsional)"}
                    </label>
                    <Input
                      value={bulkReason}
                      onChange={(e) => setBulkReason(e.target.value)}
                      placeholder={cfg.requireReason ? "Contoh: Bulk cleanup data uji..." : "Catatan tambahan..."}
                      data-testid="bulk-reason-input"
                      disabled={bulkBusy}
                    />
                  </div>
                  {bulkBusy && (
                    <div className="text-xs text-slate-600">
                      Memproses… <b>{bulkProgress.done}/{bulkProgress.total}</b>
                      {bulkProgress.failed > 0 && <span className="text-red-700"> · {bulkProgress.failed} gagal</span>}
                      <div className="mt-1 h-1.5 bg-slate-200 rounded overflow-hidden">
                        <div
                          className="h-full bg-emerald-600 transition-all"
                          style={{ width: `${bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setBulkKind(null); setBulkReason(""); }} disabled={bulkBusy}>Batal</Button>
                  <Button
                    className={cfg.color}
                    disabled={bulkBusy || bulkTargets.length === 0}
                    onClick={doBulkAction}
                    data-testid="bulk-confirm"
                  >
                    <AIcon className="h-4 w-4 mr-2" />
                    {bulkBusy ? "Memproses..." : `${cfg.label} ${bulkTargets.length} item`}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
